// packages imports
import { Controller, useFormContext } from 'react-hook-form'
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material'

// others
import { anchorOrigin, transformOrigin } from '../../../../theme/styleConstants'
import { BillingType } from '../../../../../generated'
import { enumToLabel } from '../../../../utils'
import { FC, useContext, useEffect, useState } from 'react'
import { CaseContext } from '../../../../context/CaseContext'

interface BillingTypesListProps {
  isEdit?: boolean
}

const BillingTypesList: FC<BillingTypesListProps> = ({ isEdit = false }) => {
  const { control, getValues, setValue } = useFormContext()
  const { currentCase } = useContext(CaseContext)
  const [disabled, setDisabled] = useState(false)

  const isConsultMatter = getValues('isConsultMatter')
  const billingType = currentCase?.billing?.billingType

  const billingTypes = Object.values(BillingType)
  let types: BillingType[]
  if (isConsultMatter) {
    types = [BillingType.Consult]
  } else if (isEdit && billingType === BillingType.FlatFee) {
    types = [BillingType.FlatFee, BillingType.Hybrid, BillingType.Contingency]
  } else if (isEdit && billingType === BillingType.Contingency) {
    types = [BillingType.Contingency, BillingType.FlatFee]
  } else if (isEdit && billingType === BillingType.ProBono) {
    // A Pro Bono matter can be converted to any standard billing type (Consult excluded).
    types = [
      BillingType.ProBono,
      BillingType.FlatFee,
      BillingType.Contingency,
      BillingType.ContingencyHybrid,
      BillingType.Hourly,
      BillingType.Hybrid
    ]
  } else if (isEdit && !billingType) {
    // Matter has no billing type yet — allow setting any standard type (Consult excluded).
    types = billingTypes.filter(type => type !== BillingType.Consult)
  } else {
    types = billingTypes.filter(type => type !== BillingType.Consult)
  }

  useEffect(() => {
    const selectedBillingType = getValues('billing.billingType')

    if (isConsultMatter) {
      setDisabled(true)
      return
    }

    if (
      isEdit &&
      billingType &&
      billingType !== BillingType.FlatFee &&
      billingType !== BillingType.Contingency &&
      billingType !== BillingType.ProBono &&
      billingType !== BillingType.Consult
    ) {
      setDisabled(true)
    }

    // Right now "consult" is selected but matter is already converted to normal matter
    if (selectedBillingType === BillingType.Consult) {
      setValue('billing.billingType', null)
    }

    // Only run on mount, no need to run on the changes user makes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Controller
      name="billing.billingType"
      control={control}
      render={({ field, fieldState: { error: { message } = {}, invalid } }) => (
        <FormControl variant="outlined" fullWidth required={!isConsultMatter} error={invalid} disabled={disabled}>
          <InputLabel id="role-select">Billing Type</InputLabel>
          <Select
            {...field}
            labelId="role-select"
            label="Billing Type"
            fullWidth
            MenuProps={{ anchorOrigin: anchorOrigin, transformOrigin: transformOrigin }}
          >
            {types.map((item, index) => {
              const label = enumToLabel(item)

              return (
                <MenuItem value={item} key={`${item}-${index}`}>
                  {label}
                </MenuItem>
              )
            })}
          </Select>

          {invalid && message && (
            <FormHelperText sx={{ color: theme => theme.palette.error.main }}>{message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}

export default BillingTypesList
